import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES } from "../../utils/constants";
import { getScenarioNumByPath } from "../../utils/helpers";
import CheckButton from "../../components/CheckButton";
import "./Scenario2.css";
import { IconHandClick } from "@tabler/icons-react";

/** Radio base de la lupa (px); se escala ligeramente en pantallas estrechas. */
const BASE_LENS_RADIUS = 88;

const HIDDEN_TEXT =
	"PACIENTE: Masculino, 76 años.\n" +
	"MOTIVO DE CONSULTA: Dolor en rodilla que no cede con analgésicos.\n" +
	"NOTAS INICIALES: El paciente refiere que el dolor es constante y no mejora con el reposo. Menciona dificultad para iniciar el chorro urinario.\n";

function Scenario2Context1() {
	const navigate = useNavigate();
	const location = useLocation();
	const activeScenario = getScenarioNumByPath(location.pathname);

	const stageRef = useRef(null);
	const draggingRef = useRef(false);
	const audioCtxRef = useRef(null);
	const sfxNodesRef = useRef(null);
	const sfxTimerRef = useRef(null);
	const [lens, setLens] = useState({ x: 160, y: 140 });
	const [lensRadius, setLensRadius] = useState(BASE_LENS_RADIUS);
	const [dragging, setDragging] = useState(false);

	const clampLens = useCallback(
		(x, y, r, w, h) => ({
			x: Math.min(Math.max(x, r), Math.max(r, w - r)),
			y: Math.min(Math.max(y, r), Math.max(r, h - r)),
		}),
		[]
	);

	const syncLensToPointer = useCallback(
		(clientX, clientY, offsetX = 0, offsetY = 0) => {
			const el = stageRef.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			const w = rect.width;
			const h = rect.height;
			const r = Math.min(lensRadius, w / 2 - 4, h / 2 - 4);
			const nx = clientX - rect.left - offsetX;
			const ny = clientY - rect.top - offsetY;
			setLens(clampLens(nx, ny, r, w, h));
		},
		[clampLens, lensRadius]
	);

	useLayoutEffect(() => {
		const el = stageRef.current;
		if (!el) return;

		const layout = () => {
			const w = el.clientWidth;
			const h = el.clientHeight;
			const r = Math.min(
				BASE_LENS_RADIUS,
				Math.floor(Math.min(w, h) * 0.18),
				w / 2 - 4,
				h / 2 - 4
			);
			const newR = Math.max(48, r);
			setLensRadius(newR);
			setLens(clampLens(w / 2, h / 2, newR, w, h));
		};

		layout();
		const ro = new ResizeObserver(layout);
		ro.observe(el);
		return () => ro.disconnect();
	}, [clampLens]);

	/* Lupa SVG: centro en (lens.x, lens.y); mango en diagonal 45° (abajo-derecha), como una lupa real */
	const R = lensRadius;
	const k = 0.70710678; // cos/sin 45°
	const handleLen = R * 1.25;
	const rim = Math.max(4, R * 0.07);
	const handleW = Math.max(12, R * 0.14);
	const hx1 = R * k;
	const hy1 = R * k;
	const hx2 = hx1 + handleLen * k;
	const hy2 = hy1 + handleLen * k;

	// Punto de agarre: más cerca del final del mango (evita tapar la lente con el dedo)
	const gripT = 0.72;
	const gripX = hx1 + handleLen * gripT * k;
	const gripY = hy1 + handleLen * gripT * k;

	// Hitbox (invisible) para iPad: mínimo ~44px de alto + un poco de margen.
	const hitH = Math.max(44, handleW * 2.8);
	const hitW = handleLen + Math.max(22, handleW * 0.9);
	const hitInset = Math.max(6, handleW * 0.25);
	const hitLeft = lens.x + hx1 - hitInset;
	const hitTop = lens.y + hy1 - hitH / 2;

	const startRobotSfx = useCallback((e) => {
		// Nice-to-have: debe ser 100% ignorable si no se puede reproducir.
		if (!draggingRef.current) return;
		// En desktop el usuario pidió probar en PC también: lo permitimos para "mouse" y "touch".
		// Si el navegador bloquea audio, se ignora sin afectar el juego.
		try {
			const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
			if (!AudioContextCtor) return;

			if (!audioCtxRef.current) audioCtxRef.current = new AudioContextCtor();
			const ctx = audioCtxRef.current;
			// iOS suele requerir "resume" tras gesto.
			if (ctx.state === "suspended") {
				const p = ctx.resume();
				if (p && typeof p.catch === "function") p.catch(() => {});
			}

			// Ya está sonando: no reiniciar (evita “toc toc toc”)
			if (sfxNodesRef.current) return;

			// “Bits” robóticos tipo ordenador (pitidos dobles y modulados)
			const master = ctx.createGain();
			master.gain.setValueAtTime(0.075, ctx.currentTime); // un poco más alto (se estaba quedando bajo)
			master.connect(ctx.destination);
			sfxNodesRef.current = { master };

			const playBeep = () => {
				try {
					if (!draggingRef.current) return;
					const t0 = ctx.currentTime + 0.004;
					const dur = 0.045;

					// Frecuencias discretas para “computer beeps”
					const freqs = [440, 494, 523, 587, 659, 740, 784, 880, 988, 1047];
					const base = freqs[Math.floor(Math.random() * freqs.length)];
					const second = base * (Math.random() > 0.55 ? 2 : 1.5);

					const osc = ctx.createOscillator();
					osc.type = "square";
					osc.frequency.setValueAtTime(base, t0);
					osc.frequency.linearRampToValueAtTime(base * 1.08, t0 + dur * 0.5);
					osc.frequency.linearRampToValueAtTime(base * 0.92, t0 + dur);

					const osc2 = ctx.createOscillator();
					osc2.type = "triangle";
					osc2.frequency.setValueAtTime(second, t0);
					osc2.frequency.linearRampToValueAtTime(second * 0.96, t0 + dur);

					// Ligera modulación para “robot”
					const lfo = ctx.createOscillator();
					lfo.type = "sine";
					lfo.frequency.setValueAtTime(14 + Math.random() * 10, t0);
					const lfoGain = ctx.createGain();
					lfoGain.gain.setValueAtTime(18, t0);
					lfo.connect(lfoGain);
					lfoGain.connect(osc.frequency);

					const g = ctx.createGain();
					g.gain.setValueAtTime(0.0001, t0);
					g.gain.exponentialRampToValueAtTime(1.0, t0 + 0.007);
					g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

					const hp = ctx.createBiquadFilter();
					hp.type = "highpass";
					hp.frequency.setValueAtTime(320, t0);
					hp.Q.setValueAtTime(0.7, t0);

					const bp = ctx.createBiquadFilter();
					bp.type = "bandpass";
					bp.frequency.setValueAtTime(1500, t0);
					bp.Q.setValueAtTime(3.2, t0);

					osc.connect(hp);
					osc2.connect(hp);
					hp.connect(bp);
					bp.connect(g);
					g.connect(master);

					osc.start(t0);
					osc.stop(t0 + dur);
					osc2.start(t0);
					osc2.stop(t0 + dur);
					lfo.start(t0);
					lfo.stop(t0 + dur);
				} catch {
					/* ignore */
				}
			};

			playBeep();
			sfxTimerRef.current = window.setInterval(playBeep, 190);
		} catch {
			/* ignore */
		}
	}, []);

	const stopRobotSfx = useCallback(() => {
		try {
			const ctx = audioCtxRef.current;
			const nodes = sfxNodesRef.current;
			if (!ctx || !nodes) return;
			if (sfxTimerRef.current) {
				window.clearInterval(sfxTimerRef.current);
				sfxTimerRef.current = null;
			}

			const t0 = ctx.currentTime + 0.005;
			nodes.master.gain.cancelScheduledValues(t0);
			nodes.master.gain.setValueAtTime(Math.max(0.0001, nodes.master.gain.value), t0);
			nodes.master.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.04);

			window.setTimeout(() => {
				sfxNodesRef.current = null;
			}, 70);
		} catch {
			/* ignore */
		}
	}, []);

	const onPointerDown = (e) => {
		e.currentTarget.setPointerCapture(e.pointerId);
		draggingRef.current = true;
		setDragging(true);
		startRobotSfx(e);
		syncLensToPointer(e.clientX, e.clientY, gripX, gripY);
	};

	const onPointerMove = (e) => {
		if (!draggingRef.current) return;
		// Podríamos modular el filtro aquí según la velocidad si hiciera falta.
		syncLensToPointer(e.clientX, e.clientY, gripX, gripY);
	};

	const onPointerUp = (e) => {
		draggingRef.current = false;
		setDragging(false);
		stopRobotSfx();
		try {
			e.currentTarget.releasePointerCapture(e.pointerId);
		} catch {
			/* ignore */
		}
	};

	return (
		<div className="h-100 scenario2-context1-magnifier-root d-flex flex-column pt-4">
			<div className="container-fluid px-4 px-md-5 flex-grow-1 d-flex flex-column scenario2-context1-main pt-3 pb-2 bgCircleOrange">
				<h3
					className="txt22 txt-white text-center mb-2 flex-shrink-0"
					style={{
						backgroundColor: "#fd591c",
						borderRadius: "10px",
						padding: "10px 14px",
					}}
				>
					Utilicen el siguiente resumen para responder
				</h3>
				<h5 className="mb-4 txt24 text-left my-2 gray">
					<IconHandClick size={40} color="#757575" stroke={1} /> Arrastren la lupa sobre el panel para revelar la información oculta.
				</h5>

				<div
					ref={stageRef}
					className="scenario2-context1-stage flex-shrink-0"
					role="application"
					aria-label="Área con lupa para revelar texto"
				>
					<div
						className="scenario2-context1-text-layer txtTexto1"
						style={{ whiteSpace: "pre-line" }}
					>
						{HIDDEN_TEXT}
					</div>
					<div
						className="scenario2-context1-red-glass"
					/>
					{/* Aclara la trama roja SOLO bajo la lente (mejora legibilidad, mantiene textura) */}
					<div
						className="scenario2-context1-lens-clarify"
						aria-hidden
						style={{
							WebkitClipPath: `circle(${Math.max(6, lensRadius - rim * 1.9)}px at ${lens.x}px ${lens.y}px)`,
							clipPath: `circle(${Math.max(6, lensRadius - rim * 1.9)}px at ${lens.x}px ${lens.y}px)`,
							backgroundImage: `radial-gradient(circle ${Math.max(
								10,
								lensRadius - rim * 2.2
							)}px at ${lens.x}px ${lens.y}px, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.20) 58%, rgba(255,255,255,0) 100%)`,
						}}
					/>
					{/* Texto “revelado” solo dentro de la lente (evita ver el texto normal debajo) */}
					<div
						className="scenario2-context1-text-layer scenario2-context1-text-layer--lens txtTexto1"
						aria-hidden
						style={{
							whiteSpace: "pre-line",
							WebkitClipPath: `circle(${Math.max(6, lensRadius - rim * 1.6)}px at ${lens.x}px ${lens.y}px)`,
							clipPath: `circle(${Math.max(6, lensRadius - rim * 1.6)}px at ${lens.x}px ${lens.y}px)`,
							transformOrigin: `${lens.x}px ${lens.y}px`,
							transform: "scale(1.14)",
						}}
					>
						{HIDDEN_TEXT}
					</div>
					{dragging && (
						<div
							className="scenario2-context1-lens-glow"
							aria-hidden
							style={{
								left: `${lens.x}px`,
								top: `${lens.y}px`,
								width: `${lensRadius * 2}px`,
								height: `${lensRadius * 2}px`,
							}}
						/>
					)}
					{/* Zona arrastrable: colocada sobre el mango para no tapar la lente con el dedo */}
					<div
						className="scenario2-context1-handle-hitbox"
						style={{
							left: `${hitLeft}px`,
							top: `${hitTop}px`,
							width: `${hitW}px`,
							height: `${hitH}px`,
							cursor: dragging ? "grabbing" : "grab",
						}}
						onPointerDown={onPointerDown}
						onPointerMove={onPointerMove}
						onPointerUp={onPointerUp}
						onPointerCancel={onPointerUp}
						role="button"
						aria-label="Arrastrar lupa"
					/>
					<svg
						className="scenario2-context1-lupa-svg"
						width="100%"
						height="100%"
						aria-hidden
					>
						<defs>
							<filter
								id="scenario2-lupa-shadow"
								x="-60%"
								y="-60%"
								width="220%"
								height="220%"
							>
								<feDropShadow
									dx="0"
									dy="3"
									stdDeviation="4"
									floodOpacity="0.35"
								/>
							</filter>
							<linearGradient
								id="scenario2-lupa-rim"
								x1="0%"
								y1="0%"
								x2="100%"
								y2="100%"
							>
								<stop offset="0%" stopColor="#d1d5db" />
								<stop offset="45%" stopColor="#6b7280" />
								<stop offset="100%" stopColor="#1f2937" />
							</linearGradient>
							<linearGradient
								id="scenario2-lupa-handle"
								gradientUnits="userSpaceOnUse"
								x1={hx1}
								y1={hy1}
								x2={hx2}
								y2={hy2}
							>
								<stop offset="0%" stopColor="#6b7280" />
								<stop offset="35%" stopColor="#1f2937" />
								<stop offset="100%" stopColor="#020617" />
							</linearGradient>
						</defs>
						<g
							transform={`translate(${lens.x}, ${lens.y})`}
							filter="url(#scenario2-lupa-shadow)"
						>
							{/* Mango detrás del aro */}
							<line
								x1={hx1}
								y1={hy1}
								x2={hx2}
								y2={hy2}
								stroke="url(#scenario2-lupa-handle)"
								strokeWidth={handleW}
								strokeLinecap="round"
							/>
							{/* Reflejo en el mango */}
							<line
								x1={hx1 + handleW * 0.15}
								y1={hy1 + handleW * 0.15}
								x2={hx2 - handleLen * k * 0.12}
								y2={hy2 - handleLen * k * 0.12}
								stroke="rgba(255,255,255,0.2)"
								strokeWidth={Math.max(2, handleW * 0.2)}
								strokeLinecap="round"
							/>
							{/* Aro exterior */}
							<circle
								r={R - rim / 2}
								fill="none"
								stroke="url(#scenario2-lupa-rim)"
								strokeWidth={rim}
							/>
							<circle
								r={R - rim * 1.2}
								fill="none"
								stroke="#0f172a"
								strokeWidth={Math.max(1.5, rim * 0.25)}
								opacity={0.85}
							/>
							{/* Cristal: leve viñeta (no tapa el centro transparente del juego) */}
							<circle
								r={R - rim * 1.8}
								fill="rgba(255,255,255,0.08)"
							/>
						</g>
					</svg>
				</div>

				<div className="scenario2-context1-actions flex-shrink-0 mt-3 mb-2">
					<CheckButton
						text="Siguiente"
						onClick={() => navigate(ROUTES.SCENARIO2_QUIZ1)}
						color="orange"
						ariaLabel="Ir a la primera pregunta del escenario 2"
					/>
				</div>
			</div>

			<div className="flex-shrink-0">
				<ScenarioBar activeScenario={activeScenario} />
			</div>
		</div>
	);
}

export default injectIntl(Scenario2Context1);
